import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { toast } from "@/components/ui/use-toast";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import {
  useCloseRegisterMutation,
  useGetlastRegisterSessionQuery,
} from "@/services/registerApi";
import { displayDateTime, displayValue, formatToPKR } from "@/lib/helper";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorType } from "@/types";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { AlertDiscrepancy } from "./alert-discrepnacy";
import useDocumentTitle from "@/components/ui/common/document-title";
interface CloseRegisterFormInputs {
  closing_balance: number;
  notes?: string;
}

const CloseRegister: React.FC = () => {
  useDocumentTitle("Close Register");

  const { code, counter_number } = useSelector(
    (state: RootState) => state.counter
  );

  const pos_register = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .pos_register ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const [isAlertOpen, setAlertOpen] = useState(false);
  const [discrepancy, setDiscrepancy] = useState<number | null>(null);
  const [data, setData] = useState<CloseRegisterFormInputs>({
    closing_balance: 0,
    notes: "",
  });
  const {
    data: counterData,
    isLoading,
    refetch,
    error,
  } = useGetlastRegisterSessionQuery(counter_number as number, {
    refetchOnMountOrArgChange: true, // Refetch on mount or argument change
    refetchOnFocus: true, // Refetch when the tab gains focus
    refetchOnReconnect: true, // Refetch when the browser reconnects
  });

  const [
    closeRegister,
    { isLoading: closeRegisterLoading, isError: closeRegisterError },
  ] = useCloseRegisterMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<CloseRegisterFormInputs>(
    {
      mode: "all",
      defaultValues: {
        closing_balance: undefined,
        notes: "",
      },
    }

  );
  const watcher = watch()

  const onSubmit = async (data: CloseRegisterFormInputs) => {
    console.log({ data }, "payload");
    setData(data);

    const { opening_balance, total_amount } = counterData || {};
    const expectedClosingAmount = Number(opening_balance) + Number(total_amount);
    const discrepancy = Number(data.closing_balance) - expectedClosingAmount;

    setDiscrepancy(discrepancy);

    if (discrepancy !== 0) {
      setAlertOpen(true); // Open the alert only if there is a discrepancy
      return;
    }

    handleConfirm(data); // Directly handle confirm if no discrepancy
  };

  const handleConfirm = async (data: CloseRegisterFormInputs) => {
    if (!data) return;

    console.log({ data }, "handle confirm");

    try {
      const { closing_balance, notes } = data;
      const payload = {
        id: counterData?.id as number,
        closing_balance,
        notes,
        counter_id: counter_number as number,
      };

      console.log("Payload to be sent:", payload);

      const response = await closeRegister(payload).unwrap();

      if (response) {
        // Cleanup after success
        localStorage.removeItem("isContinue");
        localStorage.removeItem("registerSession");
        toast({ variant: "success", title: "Store closed successfully" });
      }
    } catch (error) {
      handleFormError(error);
    } finally {
      setAlertOpen(false); // Ensure alert is closed after action
    }
  };

  // Utility function to handle errors
  const handleFormError = (error: unknown) => {
    console.error("Error", { error });

    const description =
      (error && typeof error === "object" && "data" in error
        ? (error as ErrorType).data?.detail ||
        (error as { data?: { message?: string } }).data?.message
        : null) || "Something went wrong.";

    toast({
      variant: "destructive",
      title: "Error in form Submission",
      description,
    });
  };


  console.log({ watcher, errors })
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-x-3 p-1 w-full ">
        <div className="flex flex-col-reverse slg:flex-row justify-center items-start w-full gap-4">
          {/* Last Closure Details */}

          <div className="w-full slg:w-[50%] flex flex-col items-center rounded-lg mb-6 lg:mb-0">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
              Current Session Details
            </h2>
            {isLoading ? (
              <div className="p-3 rounded-md w-full h-full flex flex-col items-center justify-center">
                <div>
                  <div className="flex gap-2">
                    <Skeleton className="h-4 w-[100px]" /> {/* Label */}
                    <Skeleton className="h-4 w-[150px]" /> {/* Value */}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Skeleton className="h-4 w-[130px]" /> {/* Label */}
                    <Skeleton className="h-4 w-[150px]" /> {/* Value */}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Skeleton className="h-4 w-[150px]" /> {/* Label */}
                    <Skeleton className="h-4 w-[150px]" /> {/* Value */}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Skeleton className="h-4 w-[150px]" /> {/* Label */}
                    <Skeleton className="h-4 w-[150px]" /> {/* Value */}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-md w-full h-full flex flex-col items-center justify-center">
                <div className="gap-1.5 flex flex-col">
                  <p className="text-sm flex gap-2">
                    <span className="text-sm font-semibold">Session ID:</span>{" "}
                    {displayValue(counterData?.id?.toString())}
                  </p>
                  <p className="text-sm flex gap-2">
                    <span className="text-sm font-semibold">Opening Time:</span>{" "}
                    {displayDateTime(counterData?.opening_time?.toString())}
                  </p>
                  <p className="text-sm flex gap-2">
                    <span className="text-sm font-semibold">
                      Total Cash Recieved:
                    </span>{" "}
                    {formatToPKR(counterData?.total_amount)}
                  </p>
                  <p className="text-sm flex gap-2">
                    <span className="text-sm font-semibold">
                      Total Refund Amount:
                    </span>{" "}
                    {formatToPKR(counterData?.refund_amount)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Open Register Form */}
          {pos_register !== "read" && (
            <div className="w-full slg:w-[50%]  flex flex-col items-center rounded-lg shadow-md ">
              <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
                Close Register
              </h2>
              <p className="text-center text-gray-600 mb-6">
                Please provide a closing balance to close the register
              </p>
              <div className="w-[80%] p-6 rounded-md  flex flex-col justify-between gap-3">
                <FloatingLabelInput
                  id="closingBalance"
                  type="number"
                  label="Closing Balance*"
                  {...register("closing_balance", {
                    required: "Please provide closing balance.",
                    min: {
                      value: 0,
                      message: "Closing balance must be 0 or greater.",
                    },
                    max: {
                      value: Number.MAX_SAFE_INTEGER,
                      message:
                        "Closing balance exceeds the maximum allowed value.",
                    },
                  })}
                  error={errors.closing_balance?.message}
                />

                <FloatingLabelInput
                  id="notes"
                  type="textarea"
                  label="Notes (optional)"
                  error={errors.notes?.message}
                  {...register("notes", {
                    maxLength: {
                      value: 350,
                      message: "Notes cannot exceed 350 characters",
                    },
                  })}
                />
                <div className="flex justify-center items-center">
                  <LoadingButton
                    type="submit"
                    className="bg-primary text-sm mt-6 w-40   text-white transition flex items-center gap-1  lg:mb-0 h-8 px-2 duration-300"
                    loading={closeRegisterLoading}
                    disabled={closeRegisterLoading}
                  >
                    {closeRegisterLoading ? `Closing...` : `Close Register`}
                  </LoadingButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
      <AlertDiscrepancy
        isOpen={isAlertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={() => handleConfirm(watch())}
        discrepancy={discrepancy}
      />
    </>
  );
};

export default CloseRegister;
